import clonedeep from 'lodash.clonedeep';

import { LayoutWrapper } from 'components/ui/Blog/LayoutWrapper';
import ListLayout from 'components/ui/Blog/ListLayout';
import { PageSEO } from 'components/ui/SEO';
import { POSTS_QUERY, POST_PATHS_QUERY } from 'queries';
import client from 'utils/apollo';
import { PageInfo, PostPathsType, PostsConnectionType } from 'utils/types';

type PostPageProps = {
  pageInfo: PageInfo | undefined;
  pageNumber: number;
  posts: PostsConnectionType['edges'][0]['node'][];
};

function PostPage({ pageNumber, pageInfo, posts }: PostPageProps) {
  return (
    <LayoutWrapper>
      <PageSEO
        title="Noxx Blog"
        description="A blog about pseudonymous talents"
      />
      <ListLayout
        description="Checkout our latest posts about pseudonymous economy"
        title="Our posts"
        pageNumber={pageNumber}
        pageInfo={pageInfo}
        posts={posts}
      />
    </LayoutWrapper>
  );
}

export async function getStaticProps(context: { params: { page: string } }) {
  const { params } = context;
  const pageNumber = Number(params.page);

  const { data, error } = await client.query({
    query: POSTS_QUERY,
    variables: {
      first: pageNumber > 0 ? (pageNumber + 1) * 5 : 5,
      skip: pageNumber > 0 ? pageNumber * 5 : null,
    },
  });

  const newData = clonedeep(data) as
    | { postsConnection: PostsConnectionType }
    | undefined;

  const posts = newData?.postsConnection.edges.map((edge) => edge.node);
  const pageInfo = newData?.postsConnection.pageInfo;

  return {
    props: {
      posts,
      pageInfo,
      pageNumber,
      error: error ? error.message : null,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 60 seconds
    revalidate: 60, // In seconds
  };
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
  const res = await client.query({ query: POST_PATHS_QUERY });
  const { posts } = res.data as PostPathsType;

  // // Get the paths we want to pre-render based on posts
  const numberOfPages = Math.ceil(posts.length / 5);
  const paths = Array(numberOfPages).map((_, i) => `/blog/page/${i}`);

  // We'll pre-render only these paths at build time.
  // { fallback: 'blocking' } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: 'blocking' };
}

export default PostPage;
